import React from 'react';
import { CheckCircle, Calendar, Clock, User, CreditCard, Tag, Sparkles, X, ShieldCheck } from 'lucide-react';
import { buildAppRedirectUrl } from '../../../utils/checkoutHelpers';

const PaymentConfirmation = ({
  paymentResult,
  userData,
  selectedSpecialty,
  selectedDate,
  selectedTime,
  specialtyConfig,
  selectedSlot,
  checkoutData,
  onCompleteMedicalRecord,
  onStartVerification,
  onReturnHome
}) => {
  const specialtyName = specialtyConfig[selectedSpecialty]?.name || 'Consulta';

  // Extract doctor name from API response or selectedSlot
  const professional = checkoutData?.consultationData?.result?.data?.consultation?.professional;
  const slotDoctor = selectedSlot?.professional_ids?.[0];
  const doctorName = professional
    ? `Dr(a). ${professional.name}${professional.last_name ? ' ' + professional.last_name : ''}`
    : slotDoctor?.name
      ? `Dr(a). ${slotDoctor.name}`
      : 'Profesional de la salud';

  // Format date in Spanish
  const formatDate = (date) => {
    const months = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    return `${month} ${day} ${year}`;
  };

  return (
    <div className="min-h-screen bg-daisies py-8 px-4 animate-fadeIn">
      <div className="max-w-2xl mx-auto">
        {/* Header with illustration */}
        <div className="text-center mb-8">
          {/* Success illustration */}
          <div className="mb-6 relative">
            <div className="inline-block relative">
              {/* Animated success icon */}
              <div className="w-32 h-32 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm mx-auto mb-4">
                <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center">
                  <CheckCircle className="text-mint-deep animate-pulse" size={64} strokeWidth={2.5} />
                </div>
              </div>
              {/* Decorative elements */}
              <div className="absolute -top-2 -right-2 w-8 h-8 bg-amber-pill rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
              <div className="absolute -bottom-2 -left-2 w-6 h-6 bg-brand-soft rounded-full animate-bounce" style={{ animationDelay: '0.3s' }}></div>
              <div className="absolute top-1/2 -right-4 w-4 h-4 bg-brand-tint rounded-full animate-bounce" style={{ animationDelay: '0.5s' }}></div>
            </div>
          </div>

          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
            ¡Tu consulta ha sido agendada!
          </h1>
          <p className="text-white/90 text-lg">
            Gracias por elegir daisies, esta es la información de tu consulta
          </p>
        </div>

        {/* Main card */}
        <div className="bg-white rounded-3xl overflow-hidden">
          {/* Date and Specialty section */}
          <div className="p-6 border-b border-brand-100">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-text-mute mb-1">Fecha</p>
                <p className="text-xl font-bold text-daisies">
                  {formatDate(selectedDate)}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-text-mute mb-1">Especialidad</p>
                <p className="text-xl font-bold text-text-primary">
                  {specialtyName}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6 mt-6">
              <div>
                <p className="text-sm text-text-mute mb-1">Hora</p>
                <p className="text-lg font-semibold text-text-primary">{selectedTime}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-text-mute mb-1">Duración de consulta</p>
                <p className="text-lg font-semibold text-text-primary">40 min.</p>
              </div>
            </div>
          </div>

          {/* Total section */}
          <div className="bg-daisies/5 px-6 py-4 border-b border-brand-100">
            <div className="flex justify-between items-center">
              <p className="text-sm text-text-body">Total</p>
              <div className="text-right">
                {paymentResult.appliedCoupon && (
                  <p className="text-sm text-text-mute line-through mb-1">
                    ${paymentResult.originalAmount?.toFixed(2)} MXN
                  </p>
                )}
                <p className="text-2xl font-bold text-daisies">
                  ${paymentResult.amount?.toFixed(2)} MXN
                </p>
              </div>
            </div>
          </div>

          {/* Coupon section - if applied */}
          {paymentResult.appliedCoupon && (
            <div className="bg-mint-pill px-6 py-4 border-b border-brand-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                    <Tag className="text-mint-deep" size={20} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-mint-deep">Cupón aplicado</p>
                    <p className="text-xs text-mint-deep">{paymentResult.appliedCoupon.code} - {paymentResult.appliedCoupon.name}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-mint-deep">
                    {paymentResult.appliedCoupon.discountType === 'amount'
                      ? `-$${paymentResult.appliedCoupon.discountAmount.toFixed(2)}`
                      : `-${paymentResult.appliedCoupon.discountPercentage}%`}
                  </p>
                  <p className="text-xs text-mint-deep">Descuento</p>
                </div>
              </div>
            </div>
          )}

          {/* Professional info section */}
          <div className="p-6 border-b border-brand-100">
            <p className="text-sm text-text-mute mb-3">Datos adicionales</p>
            <div className="bg-daisies/10 rounded-card p-4 flex items-center gap-4">
              <div className="w-16 h-16 bg-daisies/20 rounded-card flex items-center justify-center flex-shrink-0">
                <User className="text-daisies" size={32} />
              </div>
              <div className="flex-1">
                <p className="font-bold text-text-primary text-lg">{doctorName}</p>
                <p className="text-daisies font-semibold">{specialtyName}</p>
                <div className="flex items-center gap-1 mt-1">
                  <div className="flex gap-0.5">
                    {[...Array(5)].map((_, i) => (
                      <span key={i} className="text-amber-pill">★</span>
                    ))}
                  </div>
                  <span className="text-sm text-text-mute">1</span>
                </div>
              </div>
            </div>
          </div>

          {/* Payment info section */}
          <div className="p-6 border-b border-brand-100">
            <p className="text-sm text-text-mute mb-3">Datos de pago</p>
            <div className="bg-warm-canvas rounded-2xl p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center flex-shrink-0">
                <CreditCard className="text-daisies" size={24} />
              </div>
              <div className="flex-1">
                <p className="font-bold text-text-primary">{userData.nombre?.toUpperCase()} {userData.apellido?.toUpperCase()}</p>
                <p className="text-sm text-text-mute">Tarjeta con terminación {paymentResult.cardLast4}</p>
              </div>
            </div>
          </div>

          {/* Important notice */}
          <div className="px-6 py-4 bg-amber-pill/20 border-b border-brand-100">
            <p className="text-sm text-text-body text-center leading-relaxed">
              Si deseas cancelar y/o reagendar tu consulta, deberás hacerlo 24 horas previas a tu cita, de lo contrario no se te reembolsará
            </p>
          </div>

          {/* Transaction ID */}
          <div className="px-6 py-3 bg-warm-canvas">
            <p className="text-xs text-text-mute text-center">
              ID de transacción: <span className="font-mono font-semibold text-text-body">{paymentResult.transactionId}</span>
            </p>
          </div>
        </div>

        {/* Action buttons */}
        <div className="mt-8 space-y-4">
          <button
            onClick={onStartVerification}
            className="w-full bg-brand-deep text-white py-4 rounded-2xl font-bold text-lg transition-all flex items-center justify-center gap-2 hover:bg-brand-dark"
          >
            <ShieldCheck size={22} />
            Verificar mi cuenta
          </button>

          <button
            onClick={onCompleteMedicalRecord}
            className="w-full bg-white text-daisies py-4 rounded-card font-bold text-lg transition-all flex items-center justify-center gap-2"
          >
            <Sparkles size={20} />
            Completar Expediente Médico
          </button>

          <button
            onClick={onReturnHome}
            className="w-full bg-transparent border-2 border-white/50 text-white py-4 rounded-2xl font-semibold text-lg hover:bg-white/10 transition-all"
          >
            Volver al Inicio
          </button>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-white/80 text-sm">
            Te hemos enviado un correo de confirmación con todos los detalles.
          </p>
          <p className="text-white/60 text-xs mt-4">
            ¿Necesitas ayuda? Contáctanos en{' '}
            <a href="mailto:soporte@daisies.mx" className="text-white underline hover:text-white/80">
              soporte@daisies.mx
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentConfirmation;